"use client";

import posthog from "posthog-js";
import type { User } from "@supabase/supabase-js";
import type { Tables } from "@/models/database.types";

const LOGIN_METHOD_STORAGE_KEY = "quicknhs:last_login_method";

export type LoginMethod = "email_otp" | "google_oauth";

const PROVIDER_LOGIN_METHOD_MAP: Record<string, LoginMethod> = {
  email: "email_otp",
  google: "google_oauth",
};

export function rememberLoginMethod(method: LoginMethod) {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(LOGIN_METHOD_STORAGE_KEY, method);
}

export function consumeRememberedLoginMethod(): LoginMethod | null {
  if (typeof window === "undefined") {
    return null;
  }

  const method = window.sessionStorage.getItem(LOGIN_METHOD_STORAGE_KEY) as LoginMethod | null;

  if (method) {
    window.sessionStorage.removeItem(LOGIN_METHOD_STORAGE_KEY);
  }

  return method;
}

function getLoginMethodFromProvider(provider?: string | null) {
  if (!provider) {
    return null;
  }

  return PROVIDER_LOGIN_METHOD_MAP[provider] ?? null;
}

export function getAccessType(): "pwa" | "mobile_browser" | "desktop_browser" | "unknown" {
  if (typeof window === "undefined") return "unknown";

  // Check if running as PWA (standalone mode)
  // This covers iOS (navigator.standalone) and Android/Desktop PWA (display-mode: standalone)
  // We also check for android-app:// referrer which is sometimes present in TWAs
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
    document.referrer.includes("android-app://");

  if (isStandalone) {
    return "pwa";
  }

  // Check if running in mobile browser
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

  return isMobile ? "mobile_browser" : "desktop_browser";
}

/** How the app is displayed: "standalone" means launched as an installed PWA. */
export function getDisplayMode():
  | "standalone"
  | "fullscreen"
  | "minimal-ui"
  | "browser"
  | "unknown" {
  if (typeof window === "undefined") return "unknown";
  for (const mode of ["standalone", "fullscreen", "minimal-ui"] as const) {
    if (window.matchMedia(`(display-mode: ${mode})`).matches) return mode;
  }
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) {
    return "standalone";
  }
  return "browser";
}

const PWA_LAUNCH_SESSION_KEY = "quicknhs:pwa_launch_tracked";

/**
 * Registers access_type/display_mode as super properties so every event (pageviews included) can be
 * split by PWA vs browser, and records one pwa_launched per browser session when installed.
 */
export function registerAccessContext() {
  if (typeof window === "undefined") return;

  const accessType = getAccessType();
  posthog.register({ access_type: accessType, display_mode: getDisplayMode() });

  if (accessType === "pwa" && !window.sessionStorage.getItem(PWA_LAUNCH_SESSION_KEY)) {
    window.sessionStorage.setItem(PWA_LAUNCH_SESSION_KEY, "1");
    posthog.capture("pwa_launched", {
      access_type: accessType,
      $set: { last_access_type: accessType },
      $set_once: { first_pwa_launch_at: new Date().toISOString() },
    });
  }
}

/**
 * Identifies the user, and records user_logged_in only for a real login.
 *
 * A real login is one where the login method is known: passed in by the OTP flow, or remembered in
 * sessionStorage before the Google OAuth redirect. Session hydrates on page load have neither, so
 * they only identify; previously every full page load counted as a login.
 */
export function identifyPosthogUser(user: User, methodOverride?: LoginMethod | null) {
  if (typeof window === "undefined") {
    return;
  }

  if (!user?.id) {
    return;
  }

  const loginMethod = methodOverride ?? consumeRememberedLoginMethod();
  const alreadyIdentified = posthog.get_distinct_id() === user.id;

  if (alreadyIdentified && !loginMethod) {
    return;
  }

  const derivedMethod = loginMethod ?? getLoginMethodFromProvider(user.app_metadata?.provider);

  const name = (user.user_metadata as Record<string, unknown>)?.["full_name"] ?? undefined;

  const accessType = getAccessType();

  const personProperties = {
    email: user.email ?? undefined,
    name,
    auth_method: derivedMethod ?? "unknown",
    auth_provider: user.app_metadata?.provider ?? "unknown",
    access_type: accessType,
    last_access_type: accessType,
  };

  posthog.identify(user.id, personProperties);

  if (!loginMethod) {
    return;
  }

  posthog.capture("user_logged_in", {
    method: derivedMethod ?? "unknown",
    provider: user.app_metadata?.provider ?? "unknown",
    access_type: accessType,
  });
}

const TEST_USER_STORAGE_KEY = "quicknhs:is_test_user";

/** True when this browser last saw a test user, so replay can be off from the first page load. */
export function isKnownTestUserDevice(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(TEST_USER_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Mirrors the profile onto the PostHog person so users can be segmented (and test users excluded),
 * and stops session replay for test users, whose sessions are noise.
 */
export function syncProfileAnalytics(profile: Tables<"profiles">) {
  if (typeof window === "undefined") return;

  posthog.setPersonProperties({
    is_admin: profile.is_admin,
    is_test_user: profile.is_test_user,
    trial_status: profile.trial_status ?? "not_started",
    trial_end_date: profile.trial_end_date,
    account_suspended: profile.account_suspended,
    wallet_balance: profile.wallet_balance ?? 0,
    has_referral_code: Boolean(profile.referral_code),
  });

  const wasTestUserDevice = isKnownTestUserDevice();
  try {
    if (profile.is_test_user) {
      window.localStorage.setItem(TEST_USER_STORAGE_KEY, "1");
    } else {
      window.localStorage.removeItem(TEST_USER_STORAGE_KEY);
    }
  } catch {
    // Storage can be unavailable (private mode); the calls below still apply to this page.
  }

  if (profile.is_test_user) {
    // Unconditional: remote config may start recording after the profile has loaded.
    posthog.stopSessionRecording();
  } else if (wasTestUserDevice) {
    // A real user on a device a test user used before: replay was disabled at init, turn it back on.
    posthog.startSessionRecording();
  }
}
